import { deriveCheckpointsFromTour } from 'features/Responder/features/Tours';
import React, { useMemo } from 'react';
import { Marker, Tooltip } from 'react-leaflet';
import { buildPinIcon } from 'features/Responder/features/ResponderMap/utils/pinIcon';

const normalizeLatLng = (pt) => {
  if (!pt) return null;
  if (Array.isArray(pt) && pt.length >= 2) {
    const lat = Number(pt[0]);
    const lng = Number(pt[1]);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }
  const lat = Number(pt.lat ?? pt.latitude);
  const lng = Number(pt.lng ?? pt.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const checkpointsForTour = (tour) => {
  if (!tour) return [];
  if (Array.isArray(tour.checkpoints) && tour.checkpoints.length) {
    return tour.checkpoints
      .map((cp) => {
        const ll = normalizeLatLng(cp.coordinates || cp);
        return ll ? { ...cp, ...ll } : null;
      })
      .filter(Boolean);
  }
  if (Array.isArray(tour.nodes) && tour.nodes.length) {
    const derived = deriveCheckpointsFromTour(tour).checkpoints;
    return derived
      .map((cp) => {
        const ll = normalizeLatLng(cp.coordinates || cp);
        return ll ? { ...cp, ...ll } : null;
      })
      .filter(Boolean);
  }
  if (Array.isArray(tour.stops) && tour.stops.length) {
    return tour.stops
      .map((stop) => {
        const ll = normalizeLatLng(stop.coordinates || stop);
        return ll ? { ...stop, ...ll, label: stop.label || stop.name } : null;
      })
      .filter(Boolean);
  }
  return [];
};

export default function SelectedToursCheckpointsLayer({
  tours = [],
  selectedTourIds = [],
  activeTourId = null,
  colorMap = null,
}) {
  const toursWithChecks = useMemo(() => {
    const keys = selectedTourIds.map((id) => `${id}`);
    return tours
      .filter((gt) => keys.includes(`${gt.id}`) && `${gt.id}` !== `${activeTourId}`)
      .map((gt) => ({ tour: gt, checkpoints: checkpointsForTour(gt) }));
  }, [activeTourId, tours, selectedTourIds]);

  return toursWithChecks.map(({ tour, checkpoints }) => {
    if (!Array.isArray(checkpoints) || !checkpoints.length) return null;
    const color = colorMap?.get?.(`${tour.id}`) || '#2e7be7';
    const isActive = `${tour.id}` === `${activeTourId}`;
    const icon = buildPinIcon(color);
    return checkpoints.map((cp, idx) => (
      <Marker
        key={`tour-${tour.id}-cp-${idx}`}
        position={[cp.lat, cp.lng]}
        icon={icon}
        interactive={false}
        opacity={isActive ? 1 : 0.8}
      >
        <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
          <div className="fw-semibold">{cp.label || cp.name || `Checkpoint ${idx + 1}`}</div>
          <div className="small text-muted">{tour.name || 'Tour'}</div>
        </Tooltip>
      </Marker>
    ));
  });
}
